// ===========================================
// Achievement Badge Component
// ===========================================
// Single achievement badge display

import { Box, Typography, Tooltip, Paper } from '@mui/material';
import { Achievement, AchievementDefinition, ACHIEVEMENT_ICONS } from '../../types/gamification.types';
import { format } from 'date-fns';

type AchievementData = Achievement | AchievementDefinition;

interface AchievementBadgeProps {
  achievement: AchievementData;
  size?: 'small' | 'medium' | 'large';
  showName?: boolean;
  showDate?: boolean;
  earned?: boolean;
  onClick?: () => void;
}

function isAchievement(data: AchievementData): data is Achievement {
  return 'earnedAt' in data;
}

export default function AchievementBadge({
  achievement,
  size = 'medium',
  showName = true,
  showDate = false,
  earned = true,
  onClick,
}: AchievementBadgeProps) {
  // Get emoji for badge icon
  const getEmoji = (iconName: string): string => {
    return ACHIEVEMENT_ICONS[iconName] || '🌟';
  };

  // Size configurations
  const badgeSizes = {
    small: 48,
    medium: 64,
    large: 96,
  };

  const iconSizes = {
    small: '1.25rem',
    medium: '1.75rem',
    large: '2.5rem',
  };

  const nameSizes = {
    small: '0.625rem',
    medium: '0.75rem',
    large: '0.875rem',
  };

  const badgeSize = badgeSizes[size];
  const earnedDate = isAchievement(achievement) ? achievement.earnedAt : null;

  return (
    <Tooltip
      title={
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {achievement.name}
          </Typography>
          <Typography variant="body2">{achievement.description}</Typography>
          {earnedDate && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
              Earned: {format(new Date(earnedDate), 'PPP')}
            </Typography>
          )}
        </Box>
      }
      arrow
    >
      <Paper
        elevation={earned ? 2 : 0}
        onClick={onClick}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
          p: size === 'large' ? 1.5 : 1,
          cursor: onClick ? 'pointer' : 'default',
          transition: 'all 0.2s ease',
          opacity: earned ? 1 : 0.4,
          filter: earned ? 'none' : 'grayscale(1)',
          '&:hover': onClick
            ? {
                transform: 'translateY(-2px)',
                boxShadow: 3,
              }
            : {},
        }}
      >
        {/* Badge Circle */}
        <Box
          sx={{
            width: badgeSize,
            height: badgeSize,
            borderRadius: '50%',
            backgroundColor: earned ? achievement.badgeColor : '#E0E0E0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: earned ? `0 2px 8px ${achievement.badgeColor}40` : 'none',
            transition: 'all 0.3s ease',
          }}
        >
          <Typography sx={{ fontSize: iconSizes[size] }}>
            {getEmoji(achievement.badgeIcon)}
          </Typography>
        </Box>

        {/* Badge Name */}
        {showName && (
          <Typography
            sx={{
              fontSize: nameSizes[size],
              fontWeight: 500,
              color: earned ? 'text.primary' : 'text.secondary',
              textAlign: 'center',
              maxWidth: badgeSize + 20,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {achievement.name}
          </Typography>
        )}

        {/* Earned Date */}
        {showDate && earnedDate && (
          <Typography
            sx={{
              fontSize: '0.625rem',
              color: 'text.secondary',
            }}
          >
            {format(new Date(earnedDate), 'd MMM yyyy')}
          </Typography>
        )}
      </Paper>
    </Tooltip>
  );
}
